'use client'

import { KeyboardArrowDown } from '@mui/icons-material'
import { Box, ClickAwayListener, Popper, Typography, alpha } from '@mui/material'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import type { NavGroup } from '@core/layouts/types'

import { AbilityContext } from '~/layouts/components/acl/Can'

import VerticalNavChildRow from './VerticalNavChildRow'
import isNavItemActive from './isNavItemActive'

type Props = {
  group: NavGroup
  collapsed: boolean
  /** Persisted expanded/collapsed state keyed by group.key (only used when sidebar is expanded). */
  open: boolean
  onToggle: () => void
  /** Close mobile drawer / clear collapsed popover after navigation. */
  onNavigate?: () => void
  /**
   * Render each child row with a drag handle for manual reordering. Only
   * applied to the expanded sidebar layout — not to the collapsed-popover
   * variant (which is transient).
   */
  reorderable?: boolean
  /** Called with the source/target indices into `group.items` when a drag completes or a keyboard shortcut fires. */
  onReorder?: (fromIndex: number, toIndex: number) => void
}

const OPEN_KEY_PREFIX = 'synop-nav-group-open-c:v2'

// Per-user scoping avoids one user's picks leaking into another session on the same browser.
const buildStorageKey = (userId?: string): string => `${OPEN_KEY_PREFIX}:${userId ?? 'anon'}`

export const loadPersistedOpenState = (userId?: string): Record<string, boolean> => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(buildStorageKey(userId))
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

export const persistOpenState = (state: Record<string, boolean>, userId?: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(buildStorageKey(userId), JSON.stringify(state))
  } catch {
    // ignore
  }
}

const VerticalNavGroup = ({ group, collapsed, open, onToggle, onNavigate, reorderable, onReorder }: Props) => {
  const pathname = usePathname()
  const router = useRouter()
  const ability = useContext(AbilityContext)

  const visibleItems = useMemo(
    () =>
      group.items.filter((item) => {
        if (item.auth === false) return true
        return ability?.can(item.action, item.subject) ?? false
      }),
    [group.items, ability]
  )

  const hasActive = useMemo(() => visibleItems.some((it) => isNavItemActive(it, pathname)), [visibleItems, pathname])

  const [popoverOpen, setPopoverOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!collapsed && popoverOpen) {
      setPopoverOpen(false)
    }
  }, [collapsed, popoverOpen])

  // Warm up Next.js route prefetching when the user hovers the group header.
  // Without this, collapsed-popover children only get mounted (and therefore
  // prefetched) once the popover opens, so the first click after opening
  // pays the full cold-navigation cost.
  const prefetchChildren = useCallback(() => {
    visibleItems.forEach((item) => {
      if (!item.path || item.openInNewTab) return
      try {
        router.prefetch(item.path)
      } catch {
        // router.prefetch is a best-effort hint; swallow any errors.
      }
    })
  }, [router, visibleItems])

  if (visibleItems.length === 0) return null

  if (group.action && !(ability?.can(group.action, group.subject) ?? false)) return null

  if (collapsed) {
    return (
      <Box sx={{ position: 'relative', '&:not(:first-of-type)': { mt: 0.5 } }}>
        <Box
          component="button"
          ref={anchorRef}
          type="button"
          title={group.title}
          aria-label={group.title}
          aria-haspopup="menu"
          aria-expanded={popoverOpen}
          data-cy={`nav-group-${group.key}`}
          onClick={() => setPopoverOpen((v) => !v)}
          onMouseEnter={prefetchChildren}
          onFocus={prefetchChildren}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            py: 2,
            px: 0,
            border: 0,
            borderRadius: 1,
            cursor: 'pointer',
            fontFamily: 'inherit',
            backgroundColor: popoverOpen
              ? 'action.hover'
              : hasActive
                ? (theme) => alpha(theme.palette.accent.main, 0.08)
                : 'transparent',
            color: hasActive ? 'accent.main' : 'text.secondary',
            transition: 'background-color .12s ease-in-out',
            '&:hover': {
              backgroundColor: popoverOpen || hasActive ? undefined : 'action.hover'
            },
            '& svg': { fontSize: '1.5rem' }
          }}
        >
          {group.icon}
        </Box>
        <Popper
          open={popoverOpen}
          anchorEl={anchorRef.current}
          placement="right-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
          data-cy="nav-group-popover"
        >
          <ClickAwayListener onClickAway={() => setPopoverOpen(false)}>
            <Box
              role="menu"
              sx={{
                minWidth: 232,
                maxHeight: 'calc(100vh - 32px)',
                overflow: 'hidden',
                backgroundColor: 'background.navCard',
                borderRadius: '5px',
                boxShadow: (theme) => theme.shadows[8],
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ px: 3, pt: 2, pb: 0.5 }}>
                <Typography
                  component="span"
                  variant="h7"
                  sx={{ color: 'text.primary' }}
                >
                  {group.title}
                </Typography>
              </Box>
              <Box sx={{ px: 2, pt: 0.5, pb: 1.5, overflowY: 'auto' }}>
                {visibleItems.map((item) => (
                  <VerticalNavChildRow
                    key={`${group.key}-${item.title}`}
                    item={item}
                    inPopover
                    onNavigate={() => {
                      setPopoverOpen(false)
                      onNavigate?.()
                    }}
                  />
                ))}
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      </Box>
    )
  }

  return (
    <Box sx={{ '&:not(:first-of-type)': { mt: 3 } }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        onMouseEnter={prefetchChildren}
        onFocus={prefetchChildren}
        aria-expanded={open}
        data-cy={`nav-group-${group.key}`}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minHeight: 36,
          py: 0.75,
          px: 2,
          border: 0,
          borderRadius: 1,
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: 'transparent',
          color: 'text.primary',
          fontFamily: 'inherit',
          transition: 'background-color .12s ease-in-out',
          '&:hover': { backgroundColor: 'action.hover' },
          '& svg': { fontSize: '1.125rem', color: 'text.secondary' }
        }}
      >
        {group.icon}
        <Typography
          component="span"
          sx={{
            flex: 1,
            textAlign: 'left',
            fontSize: '0.875rem',
            lineHeight: '20px',
            letterSpacing: '0.17px',
            fontWeight: 600,
            color: 'text.primary'
          }}
        >
          {group.title}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform .14s ease'
          }}
        >
          <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
        </Box>
      </Box>
      {open ? (
        // Rail sits at the center of the group icon so submenu rows descend
        // visually from the icon. Inner `pl` gives the child labels a small
        // but unambiguous indent past the rail itself.
        <Box sx={{ position: 'relative', pl: 1.5, ml: 3, mt: 0.25 }}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              top: 4,
              bottom: 4,
              width: '1px',
              backgroundColor: 'divider'
            }}
          />
          {visibleItems.map((item, index) => (
            <VerticalNavChildRow
              key={`${group.key}-${item.title}`}
              item={item}
              onNavigate={onNavigate}
              reorderable={reorderable}
              index={index}
              itemCount={visibleItems.length}
              onReorder={onReorder}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  )
}

export default VerticalNavGroup
